<script>
import axios from 'axios';
import { useAlert } from 'dashboard/composables';

// Messello AI Copilot (EPIC 6 / Captain replacement) — an in-context chat where
// the agent asks the AI for help with the current conversation. Stateless on the
// server: we send the chat history each turn.
export default {
  name: 'MesselloCopilotPanel',
  props: {
    conversationId: { type: [Number, String], required: true },
  },
  emits: ['close', 'insert'],
  data() {
    return { messages: [], input: '', loading: false };
  },
  methods: {
    async send() {
      const text = this.input.trim();
      if (!text || this.loading) return;
      this.messages.push({ role: 'user', content: text });
      this.input = '';
      this.loading = true;
      this.scrollDown();
      try {
        const { accountId } = this.$route.params;
        const history = this.messages
          .slice(0, -1)
          .map(m => ({ role: m.role, content: m.content }));
        const { data } = await axios.post(
          `/api/v1/accounts/${accountId}/conversations/${this.conversationId}/messello_ai_copilot`,
          { message: text, history }
        );
        this.messages.push({ role: 'assistant', content: data.reply });
      } catch (error) {
        if (error?.response?.status === 403) {
          useAlert('AI Assist is available on the Growth and Scale plans.');
          this.messages.pop();
        } else {
          this.messages.push({
            role: 'assistant',
            content: 'Sorry, I could not respond right now. Please try again.',
          });
        }
      } finally {
        this.loading = false;
        this.scrollDown();
      }
    },
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.list;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<template>
  <div class="messello-copilot">
    <div class="mc-head">
      <span>🧠 AI Copilot</span>
      <button class="mc-x" type="button" @click="$emit('close')">✕</button>
    </div>
    <div ref="list" class="mc-list">
      <div v-if="!messages.length" class="mc-empty">
        Ask me to draft a reply, rephrase, or explain the customer's issue — I use
        this conversation and your help center.
      </div>
      <div v-for="(m, i) in messages" :key="i" :class="['mc-msg', m.role]">
        <div class="mc-bubble">{{ m.content }}</div>
        <button
          v-if="m.role === 'assistant'"
          class="mc-insert"
          type="button"
          @click="$emit('insert', m.content)"
        >
          Insert into reply
        </button>
      </div>
      <div v-if="loading" class="mc-msg assistant">
        <div class="mc-bubble mc-typing">…</div>
      </div>
    </div>
    <form class="mc-input" @submit.prevent="send">
      <input
        v-model="input"
        :disabled="loading"
        type="text"
        placeholder="Ask the copilot…"
      />
      <button type="submit" :disabled="loading || !input.trim()">Send</button>
    </form>
  </div>
</template>

<style scoped>
.messello-copilot {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
  max-height: 340px;
  margin: 0 8px 8px;
}
.mc-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  font-weight: 600;
  font-size: 13px;
}
.mc-x {
  border: 0;
  background: none;
  cursor: pointer;
  color: #888;
}
.mc-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mc-empty {
  color: #888;
  font-size: 13px;
  text-align: center;
  padding: 14px;
}
.mc-msg {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.mc-msg.user {
  align-items: flex-end;
}
.mc-bubble {
  max-width: 85%;
  padding: 8px 11px;
  border-radius: 10px;
  font-size: 13.5px;
  white-space: pre-wrap;
  line-height: 1.4;
}
.mc-msg.user .mc-bubble {
  background: #3b6cf6;
  color: #fff;
}
.mc-msg.assistant .mc-bubble {
  background: #f1f5f9;
  color: #0f172a;
}
.mc-insert {
  font-size: 11px;
  color: #3b6cf6;
  background: none;
  border: 0;
  cursor: pointer;
  padding: 0 2px;
  align-self: flex-start;
}
.mc-input {
  display: flex;
  gap: 6px;
  padding: 8px;
  border-top: 1px solid #eee;
}
.mc-input input {
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13.5px;
}
.mc-input button {
  background: #3b6cf6;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 0 14px;
  font-weight: 600;
  cursor: pointer;
}
.mc-input button:disabled {
  opacity: 0.5;
}
</style>
